import { API } from '@/lib/api';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getErrorMessage } from "@/lib/utils";
import { motion } from "framer-motion";
import { Users, MapPin, ArrowRight, CheckCircle2, Smartphone, Calendar, Clock, Info, CreditCard, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SavedCardsSelector from "@/components/SavedCardsSelector";
import MapLocationPicker from "@/components/MapLocationPicker";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";



const LaborerBookingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState("new");
  const [pickupCoords, setPickupCoords] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || prev.customer_name,
        customer_phone: user.phone || prev.customer_phone,
        customer_email: user.email || prev.customer_email,
      }));
    }
  }, [user]);

  const [formData, setFormData] = useState({
    service_address: "",
    job_type: "",
    hours_needed: 2,
    helpers_count: 1,
    job_date: "",
    job_time: "",
    job_description: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    notes: ""
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        customer_name: user.name || "",
        customer_phone: user.phone || "",
        customer_email: user.email || "",
      }));
    }
  }, [user]);

  const jobTypes = [
    { value: "loading", label: "Loading / Unloading", desc: "Moving items in/out of vehicles", icon: "📦" },
    { value: "packing", label: "Packing / Unpacking", desc: "Organizing and boxing items", icon: "🎁" },
    { value: "assembly", label: "Furniture Assembly", desc: "IKEA, desks, beds, etc.", icon: "🔧" },
    { value: "general_labor", label: "General Labor", desc: "Yard work, lifting, misc tasks", icon: "💪" }
  ];

  const timeSlots = [
    "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateEstimate = () => {
    const hourlyMin = 25;
    const hourlyMax = 35;
    let min = formData.hours_needed * formData.helpers_count * hourlyMin;
    let max = formData.hours_needed * formData.helpers_count * hourlyMax;
    
    return { min, max };
  };

  const goToStep2 = async () => {
    if (!formData.service_address || !formData.job_type || !formData.job_description) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!pickupCoords) {
      toast.error("Please select a valid location on the map");
      return;
    }
    const est = calculateEstimate();
    setEstimate({ min_price: est.min, max_price: est.max });
    setStep(2);
  };

  const submitBooking = async () => {
    if (!formData.customer_name || !formData.customer_phone || !formData.customer_email || !formData.job_description) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/helpers/booking`, formData);
      setStep(3);
      toast.success("Helper booking submitted!");
    } catch (error) {
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const proceedToPayment = async () => {
    if (!formData.job_description) {
      toast.error("Please fill in job description");
      return;
    }

    setPaymentLoading(true);
    try {
      const bookingResponse = await axios.post(`${API}/helpers/booking`, formData);
      const booking = bookingResponse.data;
      const serviceData = {
        booking_id: booking.id,
        booking_type: "laborer",
        amount: estimate.max_price,
        customer_email: formData.customer_email,
        customer_name: formData.customer_name,
        service_type: "laborer",
        pickup_address: formData.service_address,
        dropoff_address: "",
        pickup_coords: pickupCoords,
      };

      if (selectedCardId && selectedCardId !== "new") {
        const authResponse = await axios.post(`${API}/payments/authorize-hold`, {
          ...serviceData,
          payment_method_id: selectedCardId,
        }, { withCredentials: true });
        if (authResponse.data.success) {
          toast.success("Payment authorized! Finding your laborer...");
          navigate("/matching", {
            state: {
              booking_id: booking.id,
              booking_type: "laborer",
              service_type: "laborer",
              laborer_job_type: formData.job_type,
              pickup_lat: pickupCoords.latitude,
              pickup_lng: pickupCoords.longitude,
              pickup_address: formData.service_address,
            },
          });
        } else {
          toast.error("Payment authorization failed. Please try again.");
        }
        setPaymentLoading(false);
      } else {
        const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
          ...serviceData,
          origin_url: window.location.origin,
        });
        window.location.href = paymentResponse.data.checkout_url;
      }
    } catch (error) {
      console.error("Authorization error:", error);
      toast.error(getErrorMessage(error, "Payment authorization failed. Please try another payment method."));
      setPaymentLoading(false);
    }
  };

  const getPageTitle = () => {
    return "Book Laborers";
  };

  const getPageDescription = () => {
    return "Need an extra pair of hands? Book skilled laborers for loading, packing, assembly, or any manual labor task.";
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="laborer-booking-page">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_stackcart-app/artifacts/uldtlmbj_What-Employers-Want-In-General-Laborers.jpg')" }}
        ></div>
        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-rose-900/90 via-rose-800/85 to-rose-900/80"></div>
        <div className="absolute inset-0 noise-overlay"></div>
        <div className="section-container relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg">
              <Users className="w-8 h-8 text-rose-500" />
            </div>
            <h1 className="heading-2 text-white mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>{getPageTitle()}</h1>
            <p className="text-lg md:text-xl leading-relaxed text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
              {getPageDescription()}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section-container py-12">
        <div className="max-w-3xl mx-auto">
          {/* Job Details */}
          {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass-card p-8" data-testid="helpers-step-1">
              <h2 className="heading-3 text-emerald-950 mb-6">What Do You Need Help With?</h2>

              <div className="space-y-6">
                <div>
                  <Label className="text-emerald-950 font-medium mb-3 block">Job Type *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {jobTypes.map((job) => (
                      <button key={job.value} type="button" onClick={() => handleInputChange("job_type", job.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${formData.job_type === job.value ? "border-rose-500 bg-rose-50" : "border-stone-200 hover:border-rose-300"}`}
                        data-testid={`job-type-${job.value}`}>
                        <div className="text-2xl mb-2">{job.icon}</div>
                        <div className="font-medium text-emerald-950 text-sm">{job.label}</div>
                        <div className="text-xs text-stone-500">{job.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <MapLocationPicker
                  pickupAddress={formData.service_address}
                  onPickupChange={(val) => handleInputChange("service_address", val)}
                  onPickupCoordsChange={setPickupCoords}
                  singleLocation={true}
                  locationLabel="Service Location *"
                  pickupPlaceholder="Address where laborers are needed"
                  serviceType="laborer"
                />

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-emerald-950 font-medium mb-2 block">How Many Laborers?</Label>
                    <Select value={String(formData.helpers_count)} onValueChange={(value) => handleInputChange("helpers_count", parseInt(value))}>
                      <SelectTrigger className="input-style" data-testid="laborers-count-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map((num) => (
                          <SelectItem key={num} value={String(num)}>{num} Laborer{num > 1 ? 's' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-emerald-950 font-medium mb-2 block">How Many Hours?</Label>
                    <Select value={String(formData.hours_needed)} onValueChange={(value) => handleInputChange("hours_needed", parseInt(value))}>
                      <SelectTrigger className="input-style" data-testid="hours-needed-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <SelectItem key={num} value={String(num)}>{num} Hour{num > 1 ? 's' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-emerald-950 font-medium mb-2 block">Job Description *</Label>
                  <Textarea
                    className="input-style min-h-[80px]"
                    placeholder="Describe the work needed (e.g., loading furniture, assembling shelves...)"
                    value={formData.job_description}
                    onChange={(e) => handleInputChange("job_description", e.target.value)}
                    data-testid="job-description-input"
                  />
                </div>

                <Button className="w-full bg-rose-500 hover:bg-rose-600 text-white rounded-full py-6 font-bold" onClick={goToStep2} data-testid="continue-btn">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
            )}

            {step === 2 && estimate && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 mt-6" data-testid="payment-step">
                <h2 className="text-xl font-bold text-emerald-950 mb-4">Payment</h2>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-stone-600">Estimated Price</p>
                  <p className="text-3xl font-bold text-rose-600">${estimate.max_price.toFixed(2)} <span className="text-sm font-normal text-stone-500">CAD</span></p>
                </div>
                <SavedCardsSelector onSelect={setSelectedCardId} selectedCardId={selectedCardId} />
                <Button className="w-full bg-rose-500 hover:bg-rose-600 text-white rounded-full py-6 font-bold mt-4" onClick={proceedToPayment} disabled={paymentLoading} data-testid="pay-btn">
                  {paymentLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</> : <>Pay & Find Laborer <ArrowRight className="w-5 h-5 ml-2" /></>}
                </Button>
              </motion.div>
            )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LaborerBookingPage;
